<template>
  <nav
    v-if="totalPages > 1"
    :aria-label="$t('storeLocator.paginationAriaLabel')"
    class="flex items-center justify-between border-t border-neutral-200 pt-4"
  >
    <UiButton
      id="store-locator-pagination-previous"
      :icon="IconChevron"
      icon-side="left"
      icon-class="rotate-90"
      variant="tertiary"
      class="!rounded-none !border-none !shadow-none"
      :disabled="currentPage === 1"
      :aria-label="$t('general.previous')"
      @click="emit('page-change', currentPage - 1)"
    >
      <span class="hidden md:inline">{{ $t('general.previous') }}</span>
    </UiButton>

    <div class="flex items-center">
      <template v-for="(page, index) in visiblePages" :key="index">
        <span v-if="page === '...'" class="p-2 text-sm text-neutral-500" aria-hidden="true">
          {{ $t('general.symbols.threeDots') }}
        </span>
        <UiButton
          v-else
          :id="`store-locator-pagination-${page}`"
          variant="tertiary"
          type="neutral"
          class="!rounded-none border-x-0 border-b-0 border-t-4 !shadow-none"
          :class="currentPage === page ? '!border-primary-700 !text-primary-700' : '!border-transparent'"
          :aria-label="$t('storeLocator.paginationGoToPage', { page })"
          :aria-current="currentPage === page ? 'page' : undefined"
          @click="emit('page-change', page)"
        >
          {{ page }}
        </UiButton>
      </template>
    </div>

    <UiButton
      id="store-locator-pagination-next"
      :icon="IconChevron"
      icon-side="right"
      icon-class="-rotate-90"
      variant="tertiary"
      class="!rounded-none !border-none !shadow-none"
      :disabled="currentPage === totalPages"
      :aria-label="$t('general.next')"
      @click="emit('page-change', currentPage + 1)"
    >
      <span class="hidden md:inline">{{ $t('general.next') }}</span>
    </UiButton>
  </nav>
</template>

<script setup lang="ts">
import { IconChevron } from '#components';

import type { StoreLocatorPaginationEmits, StoreLocatorPaginationProps } from '~/components/StoreLocator/types';

const props = defineProps<StoreLocatorPaginationProps>();
const emit = defineEmits<StoreLocatorPaginationEmits>();

const visiblePages = computed((): (number | '...')[] => {
  const { currentPage, totalPages } = props;

  if (totalPages <= 4) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, '...', totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [1, '...', totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
});
</script>
